/**
 * Civil-day helpers and site-timezone anchoring (POS2-1036, shared out in POS2-1039).
 *
 * The platform resolves one timezone per request — kitchen → site → account → org → UTC —
 * and the viewer's zone is never the answer (`planning/docs/PROJECT.md` → Timezone
 * Resolution). The aggregate RPCs bucket days in that zone, so every window handed to
 * them must be built at ITS midnight or the first and last bucket are cut at the wrong hour.
 *
 * The pattern here: compute the span as inclusive civil days ("YYYY-MM-DD" strings — pure
 * calendar math with no DST edge cases), then anchor the two ends with `zonedDayStart` /
 * `zonedDayEnd`. Day strings are also what the API's `date` columns want, so callers that
 * record or display a date use them directly rather than re-deriving one from an instant.
 */

const DAY_MS = 86_400_000;

/** An inclusive span of civil days ("YYYY-MM-DD") in the resolved zone; a null bound is
 * unbounded. `useWasteFilters` returns the active window in this shape as `dayRange`. */
export type DayRange = { from: string | null; to: string | null };

/** Calendar day ("YYYY-MM-DD") of an instant in `timeZone` (en-CA renders ISO order).
 * Mirrors `localDay` in explore-chart.ts — both must agree with the RPC's site-tz buckets. */
export function zonedDay(instant: Date, timeZone: string): string {
  // Machine formatter — never locale-aware (POS2-1133). This string is a bucket key and an API
  // `date` param, not text a person reads: under `de` it would become "20.07.2026" and every
  // day bucket would silently stop matching the RPC's. Nothing here may import `lib/format.ts`.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(instant);
}

/** Milliseconds `timeZone` is ahead of UTC at `instant` (e.g. −25_200_000 for PDT). */
function tzOffsetMs(instant: Date, timeZone: string): number {
  // Machine formatter — never locale-aware (POS2-1133). The parts are read back as numbers.
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(instant);
  const at = (type: string) => Number(parts.find((p) => p.type === type)!.value);
  return (
    Date.UTC(at("year"), at("month") - 1, at("day"), at("hour"), at("minute"), at("second")) -
    instant.getTime()
  );
}

/** UTC instant of 00:00 on civil day `day` ("YYYY-MM-DD") in `timeZone`. Single-pass offset:
 * real site zones never transition at midnight, so the offset at the guess is the true one. */
export function zonedDayStart(day: string, timeZone: string): Date {
  const [y, m, d] = day.split("-").map(Number);
  const guess = Date.UTC(y, m - 1, d);
  return new Date(guess - tzOffsetMs(new Date(guess), timeZone));
}

/** UTC instant of the last millisecond of civil day `day` in `timeZone` — the inclusive
 * upper bound the RPCs expect (`logged_at <= p_date_to`). */
export function zonedDayEnd(day: string, timeZone: string): Date {
  return new Date(zonedDayStart(shiftDay(day, 1), timeZone).getTime() - 1);
}

/** Civil day `delta` days from `day`, stepped DST-free in UTC (labels are calendar dates). */
export function shiftDay(day: string, delta: number): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d) + delta * DAY_MS).toISOString().slice(0, 10);
}

/** The Monday of `day`'s week (weeks are Mon–Sun everywhere in the product). */
export function weekStartDay(day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return shiftDay(day, -((dow + 6) % 7));
}

/** The Sunday of `day`'s week. */
export function weekEndDay(day: string): string {
  return shiftDay(weekStartDay(day), 6);
}
